import { FLO_KIND } from "./floConstants";

/**
 * FLO 차트 노드 우클릭 → 다른 메뉴(화면)로 이동하면서 그 노드의 코드로 검색 조건을 미리 적용하는
 * 액션 정의. App.js 의 historyState 워처가 reduxKey 로 탭을 열고, 대상 화면은 마운트 시
 * reduxUtil.getViewState(viewName) 으로 우리가 심어둔 검색값을 읽어 초기 조회한다.
 *
 * [주의] updateViewState 는 같은 viewName 의 기존 상태에 "병합"된다(viewStates.js).
 * 따라서 이전 방문에서 남은 version·형제 검색 필드가 그대로 살아 결과를 과도하게 거를 수 있다.
 * 각 payload 는 그 화면이 reduxViewState 에서 읽는 필드를 "정확한 이름·형태"로 모두 지정해
 * 깨끗한 단일 조건으로 화면을 연다(검색 필드 형태가 화면마다 달라 개별 정의가 필요하다).
 *
 * [버전] 버전이 필요한 리포트는 version 계열 필드를 null 로 비워, 화면 자체의 기본 선택 로직
 * (가장 최신 버전 선택)이 동작하도록 한다 → 항상 최신 버전 사용.
 *
 * payload 필드 근거(검색 필드명/형태):
 * - itemSearch        : 문자열  (예: ItemMaster `searchFilters.item = itemSearch.current`)
 * - routeSearch       : Route 화면 = 객체 {routeCd} / ProdRouting 화면 = 문자열 (화면별 상이)
 * - resourceSearch    : 객체 {resCd} (ProdRouting/Resource/WIP/ResourceCapacity/ResourceProductionPlan)
 * - resource          : 객체 {resCd} (ResourceUtilization 은 resourceSearch 가 아니라 resource 사용)
 * - version/mpVersion/mpDemandVersion : 리포트별 버전 필드명 (null → 최신 자동 선택)
 */

const PAGE_RESET = { currentPage: 1 }; // 항상 1페이지부터
const resObj = (code) => ({ resCd: code, name: code }); // 자원 검색 필드 형태(필터=resCd, 표시=name)

// 품목 노드(코드 = itemCd). guard 로 자식 유무/루트(stage 0) 조건을 분기한다.
const ITEM_ACTIONS = [
  {
    id: "item", titleKey: "item", reduxKey: "INPUT_ITEM",
    payload: (c) => ({ ...PAGE_RESET, itemSearch: c, extraItemCodes: [], advancedFilters: null }),
  },
  {
    id: "bom", titleKey: "bom", reduxKey: "INPUT_BOM",
    payload: (c) => ({ ...PAGE_RESET, itemSearch: c, extraItemCodes: [], filter: {}, itemTypeSearch: null, advancedFilters: null }),
  },
  {
    id: "route", titleKey: "productionRoute", reduxKey: "INPUT_PRODUCTION_ROUTE",
    guard: (n) => n.hasChildren, // 자식(BOM)이 있는 품목만 — 생산되는 품목이 라우트를 가진다
    payload: (c) => ({ ...PAGE_RESET, itemSearch: c, extraItemCodes: [], routeSearch: null, advancedFilters: null }),
  },
  {
    id: "routing", titleKey: "routing", reduxKey: "INPUT_PRODUCTION_ROUTING",
    guard: (n) => n.hasChildren,
    payload: (c) => ({ ...PAGE_RESET, itemSearch: c, extraItemCodes: [], routeSearch: null, resourceSearch: null, advancedFilters: null }),
  },
  {
    // 대체 공급: 대체 자재가 있는 품목 또는 대체 자재 노드에서만 노출.
    // 대체 자재 노드에서는 "원자재(altSupplyOf)" 코드로 조회해야 그 대체 그룹이 보인다
    // (화면의 itemSearch 는 z_alt_supply.item_id = 원자재 기준으로 필터한다).
    id: "altSupply", titleKey: "alternateSupply", reduxKey: "INPUT_ALT_SUPPLY",
    guard: (n) => n.hasAltSupply || n.isAltSupply,
    payload: (c, n) => ({
      ...PAGE_RESET,
      itemSearch: n?.altSupplyOf ?? c,
      extraItemCodes: [],
      locationSearch: null,
      routeSearch: null,
      advancedFilters: null,
    }),
  },
  {
    id: "materialReceipt", titleKey: "materialReceiptPlan", reduxKey: "INPUT_MATERIAL_RECEIPT_PLAN",
    guard: (n) => !n.hasChildren, // 자식이 없는 품목(원자재/구매품)만
    payload: (c) => ({ ...PAGE_RESET, itemSearch: c, extraItemCodes: [], locSearch: null, supplierSearch: null, advancedFilters: null }),
  },
  {
    id: "wip", titleKey: "wip", reduxKey: "WIP",
    guard: (n) => !n.hasChildren,
    payload: (c) => ({ ...PAGE_RESET, itemSearch: c, extraItemCodes: [], resourceSearch: null, filter: { locations: null } }),
  },
  {
    id: "mrpReport", titleKey: "menuMpMrpReport", reduxKey: "MRP_REPORT",
    guard: (n) => !n.hasChildren,
    payload: (c) => ({ ...PAGE_RESET, itemSearch: c, extraItemCodes: [], version: null, filter: { locations: null } }),
  },
  {
    id: "mrpOrderList", titleKey: "menuMpMrpOrderList", reduxKey: "MRP_ORDER_LIST",
    guard: (n) => !n.hasChildren,
    payload: (c) => ({ ...PAGE_RESET, itemSearch: c, extraItemCodes: [], version: null, filter: { locations: null }, selectedSearch: null }),
  },
  {
    id: "orderTracking", titleKey: "menuMpOrderTracking", reduxKey: "ORDER_TRACKING",
    guard: (n) => n.isRoot, // 검색 루트(stage 0) 품목만
    payload: (c) => ({ ...PAGE_RESET, itemSearch: c, extraItemCodes: [], mpVersion: null, filter: { locations: null } }),
  },
  {
    // Resource Gantt: 그 품목코드로 필터 + 최신 버전(mpVersion=null → 화면이 최신 자동 선택).
    // resource/period/ganttData 를 비워 이전 방문 상태로 과도하게 거르거나 옛 캐시가 남지 않게 한다.
    id: "resourceGantt", titleKey: "menuMpResourceGantt", reduxKey: "MP_RESOURCE_GANTT",
    guard: (n) => n.hasChildren, // 생산되는 품목만 — Resource Gantt는 그 품목 생산 자원을 본다
    payload: (c) => ({ itemCd: c, mpVersion: null, resource: [], period: null, ganttData: null }),
  },
  {
    id: "demandAnalysis", titleKey: "menuMpDemandAnalysis", reduxKey: "MP_DEMAND_ANALYSIS",
    guard: (n) => n.isRoot,
    payload: (c) => ({ ...PAGE_RESET, itemSearch: c, extraItemCodes: [], mpDemandVersion: null, filter: { locations: null } }),
  },
];

// 자원 노드(코드 = resCd)
const RESOURCE_ACTIONS = [
  {
    id: "resource", titleKey: "productionResource", reduxKey: "INPUT_PRODUCTION_RESOURCE",
    payload: (c) => ({ ...PAGE_RESET, resourceSearch: resObj(c), locationSearch: null, advancedFilters: null }),
  },
  {
    id: "routing", titleKey: "routing", reduxKey: "INPUT_PRODUCTION_ROUTING",
    payload: (c) => ({ ...PAGE_RESET, resourceSearch: resObj(c), routeSearch: null, itemSearch: "", extraItemCodes: [], advancedFilters: null }),
  },
  {
    id: "resourceCapacity", titleKey: "menuResourceCapacity", reduxKey: "RESOURCE_CAPACITY",
    payload: (c) => ({ ...PAGE_RESET, resourceSearch: resObj(c), advancedFilters: null }),
  },
  {
    id: "wip", titleKey: "wip", reduxKey: "WIP",
    payload: (c) => ({ ...PAGE_RESET, resourceSearch: resObj(c), itemSearch: "", extraItemCodes: [], filter: { locations: null } }),
  },
  {
    id: "resProductionPlan", titleKey: "menuMpResourceProductionPlan", reduxKey: "RESOURCE_PRODUCTION_PLAN",
    payload: (c) => ({ ...PAGE_RESET, resourceSearch: resObj(c), itemSearch: "", extraItemCodes: [], version: null, filter: { locations: null } }),
  },
  {
    id: "resUtilization", titleKey: "menuMpResourceUtilization", reduxKey: "RESOURCE_UTILIZATION",
    payload: (c) => ({ ...PAGE_RESET, resource: resObj(c), version: null, filter: { locations: null } }),
  },
];

// 라우트 노드(코드 = routeCd). routeSearch 형태가 화면마다 다름에 주의.
const ROUTE_ACTIONS = [
  {
    id: "route", titleKey: "productionRoute", reduxKey: "INPUT_PRODUCTION_ROUTE",
    payload: (c) => ({ ...PAGE_RESET, routeSearch: { routeCd: c, name: c }, itemSearch: "", extraItemCodes: [], advancedFilters: null }),
  },
  {
    id: "routing", titleKey: "routing", reduxKey: "INPUT_PRODUCTION_ROUTING",
    payload: (c) => ({ ...PAGE_RESET, routeSearch: c, resourceSearch: null, itemSearch: "", extraItemCodes: [], advancedFilters: null }),
  },
];

const ACTIONS_BY_KIND = {
  [FLO_KIND.ITEM]: ITEM_ACTIONS,
  [FLO_KIND.RESOURCE]: RESOURCE_ACTIONS,
  [FLO_KIND.ROUTE]: ROUTE_ACTIONS,
};

/**
 * 우클릭 컨텍스트에 해당하는 이동 액션 목록을 반환한다.
 * @param {{kind: string, code: string, hasChildren?: boolean, isRoot?: boolean,
 *          isAltSupply?: boolean, altSupplyOf?: string, hasAltSupply?: boolean}} ctx
 */
export const getNodeNavActions = (ctx) => (ACTIONS_BY_KIND[ctx?.kind] || []).filter((a) => !a.guard || a.guard(ctx));
